import { BackendError, Keys, OrderExchangeData, useOrderExchangeCountdownCommon } from '@eih-netbroker/common';
import { useCallback, useMemo } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { CodeInputModal, CodeInputModalLabels } from '../../../../../shared/components/code-input-modal/CodeInputModal';
import { CapturedExchangeData } from '../../../types/orderTypes';
import { useOrderState } from '../../OrderStateContext';

type OrderTwoFactorProps = {
  capturedExchangeData?: CapturedExchangeData;
  exchangeData?: OrderExchangeData;
  on2FARetry: () => Promise<void>;
  onContinue: (exchangeData?: OrderExchangeData, smsCode?: string) => Promise<void>;
  onBack: (shouldResetFormToDefaults: boolean) => void;
  onClose: (shouldResetFormToDefaults: boolean) => void;
};

export const OrderTwoFactor = ({
  capturedExchangeData,
  exchangeData,
  onBack,
  onClose,
  on2FARetry,
  onContinue,
}: OrderTwoFactorProps) => {
  const handleOnBackFalse = useCallback(() => onBack(false), [onBack]);
  const handleModalOnCloseFalse = useCallback(() => onClose(false), [onClose]);

  const { countdown } = useOrderExchangeCountdownCommon({
    exchangeRate: capturedExchangeData?.exchangeRate,
    onExchangeRateExpired: handleOnBackFalse,
  });
  const { translation } = useLocale();
  const { smsCodeError } = useOrderState();

  const canRestartTwoFactor = useCallback((error: BackendError): boolean => {
    return error.errorCode !== Keys.ErrorCode.confirmTwoFactor.INVALID_SMS_CODE;
  }, []);

  const codeInputModalLabels = useMemo<CodeInputModalLabels>(
    () => ({
      title: translation.newOrderTwoFactor.title,
      description: translation.newOrderTwoFactor.content,
      inputLabel: translation.newOrderTwoFactor.label,
      cancelLabel: translation.general.cancel,
      submitLabel: translation.general.confirmation,
    }),
    [translation],
  );

  const handleRestartPress = useCallback(async () => {
    await on2FARetry();
  }, [on2FARetry]);

  const handleSubmit = useCallback(
    async (code: string) => await onContinue(exchangeData, code),
    [onContinue, exchangeData],
  );

  return (
    <CodeInputModal
      error={smsCodeError}
      isInitializing={false}
      onSubmit={handleSubmit}
      onCancel={handleOnBackFalse}
      onClose={handleModalOnCloseFalse}
      onRestart={handleRestartPress}
      canRestart={canRestartTwoFactor}
      countdown={countdown}
      labels={codeInputModalLabels}
    />
  );
};
